// src/pages/SchedulePage.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useData } from "../DataContext";
import { apiRequest } from "../api/http";
import { Card } from "../components/ui/Card";
import { projectDisplayLabelPretty } from "../utils/format";

function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

type ScheduleStatus = "planned" | "in_progress" | "done";
type ViewMode = "week" | "month";

type ScheduleItem = {
  id: string;
  tenant_id: string;
  project_id: string | null;
  is_non_project?: number | null; // 0/1 (DB) (may be missing depending on migration)
  task_library_id?: string | null; // NEW (may be missing depending on migration)
  task_name: string; // keep for legacy + display
  area: string | null;
  scope_notes: string | null;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  status: ScheduleStatus;
  created_at?: string;
  updated_at?: string;
};

type ScheduleAssignment = {
  id: string;
  tenant_id: string;
  schedule_item_id: string;
  resource_id: string;
  created_at?: string;
};

// Lane-based assignments returned by API (schedule_item_assignments)
type ScheduleLaneAssignment = {
  id: string;
  tenant_id: string;
  schedule_item_id: string;
  lane_type: "resource" | "internal_pool" | "subcontractor_pool" | string;
  resource_id: string | null;
  created_at?: string;
};

type ScheduleGetResponse = {
  items: ScheduleItem[];
  assignments: ScheduleAssignment[]; // legacy compatibility (resource-only)
  lane_assignments?: ScheduleLaneAssignment[]; // NEW (preferred)
};

type TaskLibraryItem = {
  id: string;
  tenant_id: string;
  name: string;
  category: string;
  color?: string | null;
  default_days?: number | null;
  status: "active" | "pending";
  created_at?: string;
  updated_at?: string;
};

const INTERNAL_GROUP_ID = "__internal_resources__";

function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

function fmtYmdUtc(d: Date) {
  const yyyy = d.getUTCFullYear();
  const mm = pad2(d.getUTCMonth() + 1);
  const dd = pad2(d.getUTCDate());
  return `${yyyy}-${mm}-${dd}`;
}

function getISOWeekRange(year: number, week: number) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7; // Sun=7
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));

  const start = new Date(week1Mon);
  start.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7);

  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  return { start, end };
}

function getISOWeekNumber(date: Date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // nearest Thursday

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
  return weekNo;
}

function fmtShort(d: Date, locale?: string) {
  const yyyy = d.getUTCFullYear();
  const mm = d.getUTCMonth();
  const dd = d.getUTCDate();
  const local = new Date(yyyy, mm, dd);
  return local.toLocaleDateString(locale || undefined, { month: "short", day: "2-digit" });
}

function weekLabelWithDates(year: number, weekNumber: number, weekWord = "Week", locale?: string) {
  const { start, end } = getISOWeekRange(year, weekNumber);
  return `${year} — ${weekWord} ${pad2(weekNumber)} (${fmtShort(start, locale)} — ${fmtShort(end, locale)})`;
}

function daysBetweenYmd(weekStartYmd: string, ymd: string) {
  const s = new Date(`${weekStartYmd}T00:00:00`);
  const d = new Date(`${ymd}T00:00:00`);
  const ms = d.getTime() - s.getTime();
  return Math.floor(ms / 86400000);
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function overlaps(
  item: { start_date: string; end_date: string },
  rangeStart: string,
  rangeEnd: string
) {
  return item.start_date <= rangeEnd && item.end_date >= rangeStart;
}

type Lane = {
  key: string;
  label: string;
  kind: "group" | "person" | "sub";
  resourceId: string;
};

export default function SchedulePage() {
  const { t: tr, i18n } = useTranslation();
  const NON_PROJECT_FILTER = "__non_project__";
  const ALL_RESOURCES_FILTER = "__all_resources__";

  const { tenantId, projects, clients, resources } = useData();

  const formTopRef = useRef<HTMLDivElement | null>(null);

  /* ======================
     Top filter
  ====================== */
  const [search, setSearch] = useState<string>("");

  /* ======================
     View + week picker
  ====================== */
  const today = useMemo(() => new Date(), []);
  const [viewMode, setViewMode] = useState<ViewMode>("week");

  const [year, setYear] = useState<number>(() => today.getFullYear());
  const [weekNumber, setWeekNumber] = useState<number>(() => getISOWeekNumber(today));
  // ======================
  // Calendar Filter + Print
  // ======================
  const [printResourceId, setPrintResourceId] = useState<string>("");
  const [printProjectId, setPrintProjectId] = useState<string>(""); // ""=All, "__non_project__"=Non-project, else projectId
  const [printStart, setPrintStart] = useState<string>("");
  const [printEnd, setPrintEnd] = useState<string>("");
  
  // Print options (client-friendly)
  const [printMode, setPrintMode] = useState<"calendar" | "table" | "both">("calendar");
  const [printShowWho, setPrintShowWho] = useState<boolean>(true);
  const [printHideInternalNonProject, setPrintHideInternalNonProject] = useState<boolean>(false);


  const [hideEmptyLanes, setHideEmptyLanes] = useState<boolean>(true);
  //const [printFiltersOpen, setPrintFiltersOpen] = useState<boolean>(true);
  // Top-level card collapses
  const [printCardOpen, setPrintCardOpen] = useState<boolean>(false); // collapsed by default
  const [scheduleFormOpen, setScheduleFormOpen] = useState<boolean>(false); // collapsed by default


  const weekRange = useMemo(() => getISOWeekRange(year, weekNumber), [year, weekNumber]);
  const weekStartYmd = useMemo(() => fmtYmdUtc(weekRange.start), [weekRange.start]);
  const weekEndYmd = useMemo(() => fmtYmdUtc(weekRange.end), [weekRange.end]);

  const weekOptions = useMemo(() => {
    const y = Number(year) || new Date().getFullYear();
    return Array.from({ length: 53 }, (_, i) => i + 1).map((w) => ({
      value: w,
      label: weekLabelWithDates(y, w, tr("schedule.viewWeek"), i18n.language),
    }));
  }, [year, tr, i18n.language]);

  const stepWeek = (delta: number) => {
    let y = Number(year) || new Date().getFullYear();
    let w = Number(weekNumber) || 1;

    w += delta;

    if (w < 1) {
      y -= 1;
      w = 53;
    } else if (w > 53) {
      y += 1;
      w = 1;
    }

    setYear(y);
    setWeekNumber(w);
  };

  /* ======================
     Data load
  ====================== */
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [assignments, setAssignments] = useState<ScheduleAssignment[]>([]);
  const [laneAssignments, setLaneAssignments] = useState<ScheduleLaneAssignment[]>([]);
  // ======================
  // Print-only data (range can differ from on-screen week)
  // ======================
  const [printBusy, setPrintBusy] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printData, setPrintData] = useState<ScheduleGetResponse | null>(null);

  // Task Library
  const [taskLibBusy, setTaskLibBusy] = useState(false);
  const [taskLibError, setTaskLibError] = useState<string | null>(null);
  const [taskLibrary, setTaskLibrary] = useState<TaskLibraryItem[]>([]);

  const load = async () => {
    try {
      setError(null);
      setBusy(true);

      const start = weekStartYmd;
      const end = weekEndYmd;

      const res = await apiRequest<{ ok: true; data: ScheduleGetResponse }>(
        "GET",
        `/api/schedule?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
        { tenantId }
      );

      const data = res.data as ScheduleGetResponse;

      setItems(data.items || []);
      setAssignments(data.assignments || []);
      setLaneAssignments((data as any).lane_assignments || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load schedule");
    } finally {
      setBusy(false);
    }
  };

  const loadForPrintRange = async (startYmd: string, endYmd: string) => {
    const s = (startYmd || "").trim();
    const e = (endYmd || "").trim();
    if (!s || !e) return;

    const rangeStart = s <= e ? s : e;
    const rangeEnd = s <= e ? e : s;

    try {
        setPrintError(null);
        setPrintBusy(true);

        const res = await apiRequest<{ ok: true; data: ScheduleGetResponse }>(
        "GET",
        `/api/schedule?start=${encodeURIComponent(rangeStart)}&end=${encodeURIComponent(rangeEnd)}`,
        { tenantId }
        );

        setPrintData(res.data as ScheduleGetResponse);
    } catch (err: any) {
        setPrintError(err?.message || "Failed to load schedule for print range");
        setPrintData({ items: [], assignments: [], lane_assignments: [] } as any);
    } finally {
        setPrintBusy(false);
    }
  };

  const loadTaskLibrary = async () => {
    try {
      setTaskLibError(null);
      setTaskLibBusy(true);

      const res = await apiRequest<{ ok: true; data: TaskLibraryItem[] }>(
        "GET",
        `/api/task_library?status=active`,
        { tenantId }
      );

      setTaskLibrary(res.data || []);
    } catch (e: any) {
      setTaskLibError(e?.message || "Failed to load task library");
      setTaskLibrary([]);
    } finally {
      setTaskLibBusy(false);
    }
  };

  useEffect(() => {
    load();
    loadTaskLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, weekStartYmd, weekEndYmd]);

  const assignedIdsByItem = useMemo(() => {
    // Prefer laneAssignments if present (supports internal_pool + subcontractor_pool)
    if ((laneAssignments || []).length) {
      const map = new Map<string, string[]>();
      (laneAssignments || []).forEach((a) => {
        const k = String((a as any).schedule_item_id || "");
        if (!k) return;

        const laneType = String((a as any).lane_type || "resource");
        const rid = (a as any).resource_id ? String((a as any).resource_id) : "";

        const arr = map.get(k) || [];

        if (laneType === "internal_pool") {
          if (!arr.includes(INTERNAL_GROUP_ID)) arr.push(INTERNAL_GROUP_ID);
        } else if (laneType === "resource") {
          if (rid && !arr.includes(rid)) arr.push(rid);
        } else if (laneType === "subcontractor_pool") {
          if (rid && !arr.includes(rid)) arr.push(rid);
        } else {
          // unknown lane types: ignore for now (safe)
        }

        map.set(k, arr);
      });
      return map;
    }

    // Fallback to legacy resource-only assignments
    const map = new Map<string, string[]>();
    (assignments || []).forEach((a) => {
      const k = a.schedule_item_id;
      const arr = map.get(k) || [];
      arr.push(a.resource_id);
      map.set(k, arr);
    });
    return map;
  }, [assignments, laneAssignments]);

  const printAssignedIdsByItem = useMemo(() => {
     const data = printData as any;
     if (!data) return new Map<string, string[]>();

      const la: ScheduleLaneAssignment[] = (data.lane_assignments || data.lane_assignments === undefined)
        ? (data.lane_assignments || [])
        : [];

      const legacy: ScheduleAssignment[] = data.assignments || [];

      // Prefer lane assignments if present
      if ((la || []).length) {
        const map = new Map<string, string[]>();
        (la || []).forEach((a: any) => {
        const k = String(a.schedule_item_id || "");
        if (!k) return;

        const laneType = String(a.lane_type || "resource");
        const rid = a.resource_id ? String(a.resource_id) : "";

        const arr = map.get(k) || [];

        if (laneType === "internal_pool") {
            if (!arr.includes(INTERNAL_GROUP_ID)) arr.push(INTERNAL_GROUP_ID);
        } else if (laneType === "resource") {
            if (rid && !arr.includes(rid)) arr.push(rid);
        } else if (laneType === "subcontractor_pool") {
            if (rid && !arr.includes(rid)) arr.push(rid);
        }

        map.set(k, arr);
        });
        return map;
       }

      // Fallback to legacy
      const map = new Map<string, string[]>();
        (legacy || []).forEach((a) => {
            const k = String(a.schedule_item_id || "");
            const rid = String(a.resource_id || "");
            if (!k || !rid) return;
            const arr = map.get(k) || [];
            if (!arr.includes(rid)) arr.push(rid);
            map.set(k, arr);
        });
        return map;
    }, [printData]);

    const isNonProject = useCallback(
    (it: ScheduleItem) => {
        return (
        Number((it as any).is_non_project || 0) === 1 ||
        !it.project_id
        );
    },
    []
  ); 


  const prettyProjectLabel = React.useCallback(
    (projectId: string | null) => {
      if (!projectId) return "";
      const p = (projects || []).find((x: any) => x.id === projectId);
      if (!p) return projectId;
      return projectDisplayLabelPretty(p as any, clients as any);
    },
    [projects, clients]
  );

  const taskNameForItem = React.useCallback(
    (it: ScheduleItem) => {
        const tid = String((it as any).task_library_id || "").trim();
        if (tid) {
        const t = (taskLibrary || []).find((x) => x.id === tid);
        if (t?.name) return t.name;
        }
        return it.task_name || "";
    },
    [taskLibrary]
  );

  const itemSearchText = React.useCallback(
    (it: ScheduleItem) => {
      const projectLabel = isNonProject(it) ? "non-project" : prettyProjectLabel(it.project_id || "");

      const laneNames =
        (assignedIdsByItem.get(it.id) || [])
          .map((rid) => {
            if (rid === INTERNAL_GROUP_ID) return "internal resources";
            const r = (resources || []).find((x: any) => x.id === rid);
            return r?.name || "";
          })
          .filter(Boolean)
          .join(" ") || "";

      return [
        it.task_name || "",
        it.area || "",
        it.scope_notes || "",
        projectLabel,
        laneNames,
        it.status || "",
      ]
        .join(" ")
        .toLowerCase();
    },
    [assignedIdsByItem, resources, prettyProjectLabel, isNonProject]
  );

  const visibleItems = useMemo(() => {
    const base = (items || []).filter((it) => overlaps(it, weekStartYmd, weekEndYmd));
    const q = (search || "").trim().toLowerCase();
    if (!q) return base;
    return base.filter((it) => itemSearchText(it).includes(q));
  }, [items, search, weekStartYmd, weekEndYmd, itemSearchText]);

  /* ======================
     Lanes (rows)
  ====================== */
  const internalPeople = useMemo(() => {
    return (resources || []).filter((r: any) => Number((r as any).isSubcontractor || 0) !== 1);
  }, [resources]);

  const subcontractors = useMemo(() => {
    return (resources || []).filter((r: any) => Number((r as any).isSubcontractor || 0) === 1);
  }, [resources]);

  const lanes: Lane[] = useMemo(() => {
    const out: Lane[] = [];
    out.push({
      key: "lane_internal_group",
      label: tr("schedule.internalResources"),
      kind: "group",
      resourceId: INTERNAL_GROUP_ID,
    });

    internalPeople.forEach((r: any) => {
      out.push({
        key: `lane_person_${r.id}`,
        label: r.name,
        kind: "person",
        resourceId: r.id,
      });
    });

    subcontractors.forEach((r: any) => {
      out.push({
        key: `lane_sub_${r.id}`,
        label: r.name,
        kind: "sub",
        resourceId: r.id,
      });
    });

    return out;
  }, [internalPeople, subcontractors, tr]);

  const resourceOptions = useMemo(() => {
    return (lanes || []).map((l) => {
      const kindLabel =
        l.kind === "group" ? "Group" : l.kind === "sub" ? "Subcontractor" : "Internal";
      return { id: l.resourceId, label: l.label, kindLabel };
    });
  }, [lanes]);


  /* ======================
     Card 1 form state (Add/Edit)
  ====================== */
  const [editingId, setEditingId] = useState<string | null>(null);

  const [nonProjectTask, setNonProjectTask] = useState<boolean>(false);
  const [formProjectId, setFormProjectId] = useState<string>("");

  const [taskLibraryId, setTaskLibraryId] = useState<string>("");
  const [taskName, setTaskName] = useState<string>(""); // keep legacy/display sync

  const [area, setArea] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  // Manage Tasks modal
    const [showManageTasks, setShowManageTasks] = useState(false);
    const [taskManagerSearch, setTaskManagerSearch] = useState("");
    const [taskEditId, setTaskEditId] = useState<string | null>(null);
    const [taskEditName, setTaskEditName] = useState("");
    const [taskEditCategory, setTaskEditCategory] = useState("");
    const [taskEditBusy, setTaskEditBusy] = useState(false);

    const isSystemTenant = String(tenantId || "").toLowerCase() === "system";
    const canEditTask = (t: TaskLibraryItem) =>
    t.tenant_id === tenantId || (isSystemTenant && t.tenant_id === "global");
    const isGlobalTask = (t: TaskLibraryItem) => String(t.tenant_id || "") === "global";

  const [newTaskCategory, setNewTaskCategory] = useState("");
  const [newTaskBusy, setNewTaskBusy] = useState(false);

  // src/pages/SchedulePage.tsx  (CHUNK 2 / ~250 lines)

  const [startDate, setStartDate] = useState<string>(weekStartYmd);
  const [endDate, setEndDate] = useState<string>(weekEndYmd);
  const [status, setStatus] = useState<ScheduleStatus>("planned");

  const [assignedIds, setAssignedIds] = useState<string[]>([]);
  type AssignPanel = "people" | "subs" | null;
  const [openAssignPanel, setOpenAssignPanel] = useState<AssignPanel>(null);

  const taskLibraryByCategory = useMemo(() => {
    const map = new Map<string, TaskLibraryItem[]>();
    (taskLibrary || []).forEach((t) => {
      const cat = (t.category || "").trim() || "Other";
      const arr = map.get(cat) || [];
      arr.push(t);
      map.set(cat, arr);
    });
    Array.from(map.values()).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [taskLibrary]);

  const taskManagerList = useMemo(() => {
  const q = (taskManagerSearch || "").trim().toLowerCase();
  const base = taskLibrary || [];
  if (!q) return base;
  return base.filter((t) => {
    const text = `${t.name || ""} ${t.category || ""} ${t.tenant_id || ""}`.toLowerCase();
    return text.includes(q);
  });
  }, [taskLibrary, taskManagerSearch]);

const taskManagerByCategory = useMemo(() => {
  const map = new Map<string, TaskLibraryItem[]>();
  (taskManagerList || []).forEach((t) => {
    const cat = (t.category || "").trim() || "Other";
    const arr = map.get(cat) || [];
    arr.push(t);
    map.set(cat, arr);
  });
  Array.from(map.values()).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}, [taskManagerList]);


  const canSubmit = useMemo(() => {
    const hasLibraryPick = Boolean((taskLibraryId || "").trim());
    const hasLegacyName = (taskName || "").trim().length > 0;

    const nameOk = editingId ? (hasLibraryPick || hasLegacyName) : hasLibraryPick;

    const datesOk = Boolean(startDate && endDate && endDate >= startDate);
    const projOk = nonProjectTask ? true : Boolean((formProjectId || "").trim());
    const assignedOk = assignedIds.length > 0;
    return nameOk && datesOk && projOk && assignedOk && !busy;
  }, [
    taskLibraryId,
    taskName,
    editingId,
    startDate,
    endDate,
    nonProjectTask,
    formProjectId,
    assignedIds,
    busy,
  ]);

  useEffect(() => {
    // Keep add-form dates aligned to current week only when NOT editing
    if (editingId) return;
    setStartDate(weekStartYmd);
    setEndDate(weekEndYmd);
  }, [weekStartYmd, weekEndYmd, editingId]);

  useEffect(() => {
    if (editingId) setScheduleFormOpen(true);
  }, [editingId]);

    useEffect(() => {
    // Keep print range aligned to the currently selected week by default
    setPrintStart(weekStartYmd);
    setPrintEnd(weekEndYmd);
  }, [weekStartYmd, weekEndYmd]);


  const resetForm = () => {
    setEditingId(null);
    setNonProjectTask(false);
    setFormProjectId("");
    setTaskLibraryId("");
    setTaskName("");
    setArea("");
    setNotes("");
    setStartDate(weekStartYmd);
    setEndDate(weekEndYmd);
    setStatus("planned");
    setAssignedIds([]);
    setOpenAssignPanel(null);
  };

  const toggleAssigned = (rid: string) => {
    setAssignedIds((prev) => {
      if (prev.includes(rid)) return prev.filter((x) => x !== rid);
      return [...prev, rid];
    });
  };

  const beginEdit = (it: ScheduleItem) => {
    setEditingId(it.id);

    const np = isNonProject(it);
    setNonProjectTask(np);
    setFormProjectId(np ? "" : String(it.project_id || ""));

    setTaskLibraryId(String((it as any).task_library_id || ""));
    setTaskName(taskNameForItem(it) || "");

    setArea(it.area || "");
    setNotes(it.scope_notes || "");
    setStartDate(it.start_date);
    setEndDate(it.end_date);
    setStatus(it.status || "planned");

    setAssignedIds(assignedIdsByItem.get(it.id) || []);

    setTimeout(() => {
      formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const createTask = async () => {
    try {
      setNewTaskBusy(true);

      const name = (newTaskName || "").trim();
      const category = (newTaskCategory || "").trim();

      if (!name) throw new Error("Task name is required.");
      if (!category) throw new Error("Category is required.");

      const res = await apiRequest<{ ok: true; data: TaskLibraryItem }>(
        "POST",
        "/api/task_library",
        { tenantId, body: { name, category, status: "active" } }
      );

      await loadTaskLibrary();

      setTaskLibraryId(res.data.id);
      setTaskName(res.data.name);

      setShowAddTask(false);
      setNewTaskName("");
      setNewTaskCategory("");
    } catch (e: any) {
      setError(e?.message || "Failed to create task");
    } finally {
      setNewTaskBusy(false);
    }
  };

  const beginTaskEdit = (t: TaskLibraryItem) => {
  setTaskEditId(t.id);
  setTaskEditName(t.name || "");
  setTaskEditCategory(t.category || "");
  };

const cancelTaskEdit = () => {
  setTaskEditId(null);
  setTaskEditName("");
  setTaskEditCategory("");
};

const saveTaskEdit = async () => {
  if (!taskEditId) return;
  try {
    setTaskEditBusy(true);

    const name = (taskEditName || "").trim();
    const category = (taskEditCategory || "").trim();
    if (!name) throw new Error("Task name is required.");
    if (!category) throw new Error("Category is required.");

    const res = await apiRequest<any>(
      "PATCH",
      `/api/task_library/${encodeURIComponent(taskEditId)}`,
      { tenantId, body: { name, category } }
    );

    if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed to update task");

    await loadTaskLibrary();
    cancelTaskEdit();
  } catch (e: any) {
    setError(e?.message || "Failed to update task");
  } finally {
    setTaskEditBusy(false);
  }
};

const deleteTask = async (t: TaskLibraryItem) => {
  const ok = window.confirm(tr("schedule.deleteTaskConfirm", { name: t.name }));
  if (!ok) return;

  try {
    setTaskEditBusy(true);

    const res = await apiRequest<any>(
      "DELETE",
      `/api/task_library/${encodeURIComponent(t.id)}`,
      { tenantId }
    );

    if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed to delete task");

    // If the currently selected schedule form task got deleted, clear selection
    if (taskLibraryId === t.id) {
      setTaskLibraryId("");
      setTaskName("");
    }

    await loadTaskLibrary();
    if (taskEditId === t.id) cancelTaskEdit();
  } catch (e: any) {
    setError(e?.message || "Failed to delete task");
  } finally {
    setTaskEditBusy(false);
  }
};



  const save = async () => {
    try {
      setError(null);
      setBusy(true);

      const libId = (taskLibraryId || "").trim();
      const name = (taskName || "").trim();

      // New items must use library selection (prevents spelling mistakes)
      if (!editingId && !libId) throw new Error("Task is required. Select a task or add a new one.");

      // Editing legacy items: allow existing task_name if present
      if (editingId && !libId && !name) throw new Error("Task is required.");

      const s = startDate;
      const e = endDate;
      if (!s || !e) throw new Error("Start and end dates are required.");
      if (e < s) throw new Error("End date cannot be before start date.");

      const pid = nonProjectTask ? null : (formProjectId || "").trim() || null;

      // Keep legacy behavior: API may require project_id even for non-project until migration/API update.
      // We send project_id null when non-project, but if API returns error we'll show it clearly.
      if (!nonProjectTask && !pid) throw new Error("Project is required (or check Non-project task).");

      if (!assignedIds.length)
        throw new Error("Assign at least one resource (Internal group, individual, or subcontractor).");

      // Convert UI assignedIds into lane-based assignments expected by your API:
      // - INTERNAL_GROUP_ID => includeInternalPool
      // - individual internal resources => assignedResourceIds
      // - subcontractor resource rows => subcontractorIds
      const includeInternalPool = assignedIds.includes(INTERNAL_GROUP_ID);

      const assignedResourceIds = assignedIds
        .filter((id) => id !== INTERNAL_GROUP_ID)
        .filter((id) => internalPeople.some((r: any) => String(r.id) === String(id)));

      const subcontractorIds = assignedIds
        .filter((id) => id !== INTERNAL_GROUP_ID)
        .filter((id) => subcontractors.some((r: any) => String(r.id) === String(id)));

      const body: any = {
        project_id: pid,
        is_non_project: nonProjectTask ? 1 : 0,
        task_library_id: libId || null,
        task_name: name || null, // keep legacy compatibility
        area: (area || "").trim() || null,
        scope_notes: (notes || "").trim() || null,
        start_date: s,
        end_date: e,
        status,

        // lane-based assignments expected by your API
        includeInternalPool,
        assignedResourceIds,
        subcontractorIds,
      };

      if (editingId) {
        const res = await apiRequest<any>(
          "PATCH",
          `/api/schedule/${encodeURIComponent(editingId)}`,
          { tenantId, body }
        );
        if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed to update schedule item");
      } else {
        const res = await apiRequest<any>("POST", "/api/schedule", { tenantId, body });
        if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed to create schedule item");
      }

      resetForm();
      setScheduleFormOpen(false); // collapse after successful save so focus returns to calendar
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to save schedule item");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = window.confirm(tr("schedule.deleteItemConfirm"));
    if (!ok) return;

    try {
      setError(null);
      setBusy(true);

      const res = await apiRequest<any>(
        "DELETE",
        `/api/schedule/${encodeURIComponent(id)}`,
        { tenantId }
      );
      if (!(res as any)?.ok) throw new Error((res as any)?.error || "Failed to delete schedule item");

      if (editingId === id) resetForm();
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to delete schedule item");
    } finally {
      setBusy(false);
    }
  };

/* ======================
   Week view rendering helpers
====================== */

type CardSpan = {
  id: string;
  startIdx: number;
  endIdx: number;
  item: ScheduleItem;
};

const weekDays = useMemo(() => {
  const labels = [tr("schedule.dayMon"), tr("schedule.dayTue"), tr("schedule.dayWed"), tr("schedule.dayThu"), tr("schedule.dayFri"), tr("schedule.daySat"), tr("schedule.daySun")];
  const start = new Date(`${weekStartYmd}T00:00:00`);
  return labels.map((lbl, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return {
      key: `${i}`,
      label: lbl,
      ymd: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    };
  });
}, [weekStartYmd, tr]);

const buildTracks = useCallback((cards: CardSpan[]) => {
  const tracks: CardSpan[][] = [];
  const sorted = [...cards].sort(
    (a, b) => a.startIdx - b.startIdx || a.endIdx - b.endIdx
  );

  sorted.forEach((c) => {
    let placed = false;
    for (const t of tracks) {
      const last = t[t.length - 1];
      if (!last || c.startIdx > last.endIdx) {
        t.push(c);
        placed = true;
        break;
      }
    }
    if (!placed) tracks.push([c]);
  });

  return tracks;
}, []);

const calendarRows = useMemo(() => {
  return lanes.map((lane) => {
    const laneItems = visibleItems.filter((it) => {
      const ids = assignedIdsByItem.get(it.id) || [];
      return ids.includes(lane.resourceId);
    });

    const cards: CardSpan[] = laneItems.map((it) => {
      const startIdx = clamp(daysBetweenYmd(weekStartYmd, it.start_date), 0, 6);
      const endIdx = clamp(daysBetweenYmd(weekStartYmd, it.end_date), 0, 6);
      return { id: it.id, startIdx, endIdx, item: it };
    });

    const tracks = buildTracks(cards);
    return { lane, tracks };
  });
}, [
  lanes,
  visibleItems,
  assignedIdsByItem,
  weekStartYmd,
  buildTracks,
]);

const calendarRowsVisible = useMemo(() => {
  if (!hideEmptyLanes) return calendarRows;

  // keep internal group visible even if empty
  return (calendarRows || []).filter((r) => {
    if (r?.lane?.resourceId === INTERNAL_GROUP_ID) return true;
    return (r.tracks || []).length > 0;
  });
}, [calendarRows, hideEmptyLanes]);

const statusBadge = useCallback((s: ScheduleStatus) => {
  if (s === "done") return "badge badgeGood";
  if (s === "in_progress") return "badge badgeWarn";
  return "badge";
}, []);

 const printItems = useMemo(() => {
  const rs = (printStart || "").trim();
  const re = (printEnd || "").trim();
  const rid = (printResourceId || "").trim();

  if (!rs || !re) return [];

  const rangeStart = rs <= re ? rs : re;
  const rangeEnd = rs <= re ? re : rs;

  const projFilter = (printProjectId || "").trim();

  const sourceItems: ScheduleItem[] = (printData?.items || items || []) as any;
  const sourceAssigned = printData ? printAssignedIdsByItem : assignedIdsByItem;

  const base = (sourceItems || []).filter((it) => overlaps(it, rangeStart, rangeEnd));

  const filteredByResource =
    rid && rid !== ALL_RESOURCES_FILTER
      ? base.filter((it) => {
          const ids = sourceAssigned.get(it.id) || [];
          return ids.includes(rid);
        })
      : base;

  const filteredByProject = filteredByResource.filter((it) => {
    if (!projFilter) return true;
    if (projFilter === NON_PROJECT_FILTER) return isNonProject(it);
    return String(it.project_id || "") === projFilter;
  });

  // Client-facing cleanup (only affects print)
  const filteredForClient =
    !printShowWho && printHideInternalNonProject
      ? filteredByProject.filter((it) => !isNonProject(it))
      : filteredByProject;

  return filteredForClient.sort((a, b) => {
    if (a.start_date !== b.start_date) return a.start_date.localeCompare(b.start_date);
    return taskNameForItem(a).localeCompare(taskNameForItem(b));
  });
 }, [
  items,
  assignedIdsByItem,
  printData,
  printAssignedIdsByItem,
  printStart,
  printEnd,
  printResourceId,
  printProjectId,
  taskNameForItem,
  isNonProject,
  printShowWho,
  printHideInternalNonProject,
 ]);
const printGroups = useMemo(() => {
  const map = new Map<string, ScheduleItem[]>();

  (printItems || []).forEach((it) => {
    const key = isNonProject(it)
      ? "Non-Project"
      : (prettyProjectLabel(it.project_id) || "Unknown Project");

    const arr = map.get(key) || [];
    arr.push(it);
    map.set(key, arr);
  });

  const groups = Array.from(map.entries()).map(([projectLabel, list]) => {
    const sorted = [...list].sort((a, b) => {
      if (a.start_date !== b.start_date) return a.start_date.localeCompare(b.start_date);
      return taskNameForItem(a).localeCompare(taskNameForItem(b));
    });
    return { projectLabel, items: sorted };
  });

  groups.sort((a, b) => a.projectLabel.localeCompare(b.projectLabel));
  return groups;
}, [printItems, prettyProjectLabel, taskNameForItem, isNonProject]);

  /* ======================
     Print (Swimlane / screen-style)
     - one week per page
     - lanes across days
     - bars span start→end
  ====================== */

  const parseYmdUtc = useCallback((ymd: string) => {
    const parts = (ymd || "").split("-");
    const y = Number(parts[0] || 0);
    const m = Number(parts[1] || 1);
    const d = Number(parts[2] || 1);
    return new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  }, []);

  const fmtYmdFromUtcDate = useCallback((dt: Date) => {
    const yyyy = dt.getUTCFullYear();
    const mm = pad2(dt.getUTCMonth() + 1);
    const dd = pad2(dt.getUTCDate());
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const addDaysUtc = useCallback((dt: Date, days: number) => {
    const out = new Date(dt);
    out.setUTCDate(out.getUTCDate() + days);
    return out;
  }, []);

  const startOfWeekMonUtc = useCallback((dt: Date) => {
    const day = dt.getUTCDay(); // 0=Sun..6=Sat
    const delta = day === 0 ? -6 : 1 - day; // shift to Monday
    const out = new Date(dt);
    out.setUTCDate(out.getUTCDate() + delta);
    out.setUTCHours(0, 0, 0, 0);
    return out;
  }, []);

  const printRangeStartYmd = useMemo(() => {
    const rs = (printStart || "").trim();
    const re = (printEnd || "").trim();
    if (!rs || !re) return "";
    return rs <= re ? rs : re;
  }, [printStart, printEnd]);

  const printRangeEndYmd = useMemo(() => {
    const rs = (printStart || "").trim();
    const re = (printEnd || "").trim();
    if (!rs || !re) return "";
    return rs <= re ? re : rs;
  }, [printStart, printEnd]);

  const printLanesForSwim = useMemo(() => {
  // If we're hiding "who", do not use resource lanes at all
  if (!printShowWho) {
    return [];
  }

  const rid = (printResourceId || "").trim();
  if (rid && rid !== ALL_RESOURCES_FILTER) {
    return (lanes || []).filter((l) => String(l.resourceId) === String(rid));
  }
  return lanes || [];
  }, [lanes, printResourceId, printShowWho]);

  type PrintSwimLaneRow = { lane: Lane; tracks: CardSpan[][] };
  type PrintSwimWeek = {
    weekStartYmd: string;
    weekEndYmd: string;
    days: { label: string; ymd: string }[];
    rows: PrintSwimLaneRow[];
  };

  const printSwimWeeks = useMemo((): PrintSwimWeek[] => {
    if (!printRangeStartYmd || !printRangeEndYmd) return [];

    const labels = [tr("schedule.dayMon"), tr("schedule.dayTue"), tr("schedule.dayWed"), tr("schedule.dayThu"), tr("schedule.dayFri"), tr("schedule.daySat"), tr("schedule.daySun")];

    const startDt = startOfWeekMonUtc(parseYmdUtc(printRangeStartYmd));
    const endDt = parseYmdUtc(printRangeEndYmd);

    const weeks: PrintSwimWeek[] = [];

    for (let cur = new Date(startDt); cur.getTime() <= endDt.getTime(); cur = addDaysUtc(cur, 7)) {
      const weekStart = new Date(cur);
      const weekEnd = addDaysUtc(weekStart, 6);

      const weekStartYmd = fmtYmdFromUtcDate(weekStart);
      const weekEndYmd = fmtYmdFromUtcDate(weekEnd);

      const days = labels.map((lbl, i) => {
        const d = addDaysUtc(weekStart, i);
        return { label: lbl, ymd: fmtYmdFromUtcDate(d) };
      });

      // Only items that overlap this week
      const weekItems = (printItems || []).filter((it) => overlaps(it, weekStartYmd, weekEndYmd));

      const rows: PrintSwimLaneRow[] = (printLanesForSwim || []).map((lane) => {
        const laneItems = weekItems.filter((it) => {
          const ids = printAssignedIdsByItem.get(it.id) || [];
          return ids.includes(lane.resourceId);
        });

        const cards: CardSpan[] = laneItems.map((it) => {
          const startIdx = clamp(daysBetweenYmd(weekStartYmd, it.start_date), 0, 6);
          const endIdx = clamp(daysBetweenYmd(weekStartYmd, it.end_date), 0, 6);
          return { id: it.id, startIdx, endIdx, item: it };
        });

        const tracks = buildTracks(cards);
        return { lane, tracks };
      });

      // Respect "hide empty lanes" even in print (keeps internal group visible)
      const rowsVisible = hideEmptyLanes
        ? rows.filter((r) => r.lane.resourceId === INTERNAL_GROUP_ID || (r.tracks || []).length > 0)
        : rows;

      weeks.push({
        weekStartYmd,
        weekEndYmd,
        days,
        rows: rowsVisible,
      });
    }

    return weeks;
  }, [
    printRangeStartYmd,
    printRangeEndYmd,
    parseYmdUtc,
    startOfWeekMonUtc,
    addDaysUtc,
    fmtYmdFromUtcDate,
    printItems,
    printLanesForSwim,
    printAssignedIdsByItem,
    buildTracks,
    hideEmptyLanes,
    tr,
  ]);

  /* ======================
     Render
  ====================== */
  return (
    <div className="container">
      <style>{`

        .printToolsBox{
        border: 1px dashed rgba(0,0,0,0.18);
        background: rgba(255,255,255,0.65);
        border-radius: 16px;
        padding: 10px;
        margin: 10px 0 12px 0;
        }
        .printToolsTitle{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:10px;
        margin-bottom: 8px;
        }

        .printCollapseBtn{
          height: 34px;
          border-radius: 999px;
          padding: 0 10px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          cursor: pointer;
          font-weight: 900;
          color: var(--text);
          box-shadow: var(--shadow-sm);
        }

        .printToolsTitle .label{
        margin:0;
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
        }
        .printToolsHint{
        font-size: 12px;
        font-weight: 800;
        color: var(--muted);
        }

        /* Calendar tools row */
        .calToolsRow{
        display:flex;
        align-items:flex-end;
        justify-content:space-between;
        gap: 12px;
        flex-wrap: wrap;
        margin: 8px 0 12px 0;
        }

        .calToolsLeft{
        display:flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items:flex-end;
        }

        .calToolsLeft .field{
        min-width: 180px;
        }

        .calToolsRight{
        display:flex;
        gap: 10px;
        align-items:center;
        }

        /* Make week picker buttons align nicely with dropdown */
        .weekPickerGroup{
        display:flex;
        align-items:center;
        gap: 8px;
        }

        .weekPickerGroup .toggleBtn{
        height: 40px;
        width: 40px;
        padding: 0;
        }

        .scheduleHeaderRow{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:12px;
          margin-bottom: 12px;
        }
          
        .scheduleCardSpacer{
        margin-top: 14px;
        }

        .scheduleHeaderLeft h2{ margin:0; }
        .scheduleHeaderLeft .muted{ margin-top: 4px; }
        .scheduleHeaderRight{
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap:wrap;
          justify-content:flex-end;
        }
        .searchInput{
          width: min(360px, 72vw);
          height: 40px;
          border-radius: 12px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          padding: 0 12px;
          color: var(--text);
          box-shadow: var(--shadow-sm);
        }

        /* Card 1 layout */
        /* Card 1 layout (responsive, no squeeze) */
        .scheduleFormGrid{
        display:grid;
        gap: 14px;
        align-items:start;

        grid-template-columns: 1.1fr 1fr 1.2fr;
        grid-template-areas: "left mid right";
        }

        .scheduleFormCol{ min-width: 0; }
        .scheduleFormColLeft{ grid-area: left; }
        .scheduleFormColMid{ grid-area: mid; }
        .scheduleFormColRight{ grid-area: right; }

        /* Tablet: keep fields readable by moving Assign to its own row */
        @media (max-width: 1100px){
        .scheduleFormGrid{
            grid-template-columns: 1fr 1fr;
            grid-template-areas:
            "left mid"
            "right right";
        }
        }

        /* Mobile: stack all 3 sections */
        @media (max-width: 720px){
        .scheduleFormGrid{
            grid-template-columns: 1fr;
            grid-template-areas:
            "left"
            "mid"
            "right";
        }
        }

        .field{ display:flex; flex-direction:column; gap:6px; }
        .labelRow{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
        .label{
          font-weight: 700;
          font-size: 13px;
          color: var(--text);
        }
        .reqStar{ color: #ff6b6b; margin-left: 4px; }
        .input, .select, textarea{
          width: 100%;
          border-radius: 12px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          padding: 10px 12px;
          color: var(--text);
          box-shadow: var(--shadow-sm);
        }
        textarea{ min-height: 86px; resize: vertical; }
        .inlineRow{ display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
        .checkRow{ display:flex; align-items:center; gap:10px; user-select:none; }
        .checkRow input{ transform: translateY(1px); }

        /* Resource picker */
        .assignBox{
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.86);
          border-radius: 14px;
          padding: 10px;
          box-shadow: var(--shadow-sm);
        }
        .assignHeader{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          margin-bottom: 8px;
        }
        .assignHeader h4{
          margin:0;
          font-size: 13px;
          font-weight: 800;
          color: var(--text);
        }
        .assignGroup{
          margin-top: 10px;
        }
        .groupTitleBtn{
          width:100%;
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:10px;
          background: transparent;
          border: 0;
          padding: 8px 6px;
          cursor: pointer;
          color: var(--text);
          font-weight: 800;
        }

        .assignList{ display:flex; flex-direction:column; gap:8px; padding: 4px 6px 8px; }
        .assignItem{
          display:flex;
          align-items:center;
          gap:10px;
          padding: 4px 2px;
        }
        .assignItem .mutedName{ color: var(--muted); font-size: 12px; font-weight: 700; }
        .caret{ opacity: 0.75; font-size: 12px; }

        .formActions{
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:10px;
          margin-top: 10px;
        }
        .btn{
          height: 40px;
          border-radius: 12px;
          border: 1px solid var(--border);
          padding: 0 14px;
          cursor:pointer;
          background: rgba(255,255,255,0.92);
          box-shadow: var(--shadow-sm);
          font-weight: 800;
          color: var(--text);
        }
        .btnPrimary{
          background: rgba(30, 64, 175, 0.10);
          border-color: rgba(30, 64, 175, 0.35);
          color: var(--text);
        }
        .btn[disabled]{
          opacity: 1;
          cursor: not-allowed;
          background: var(--table-row-hover);
          border-color: var(--border);
          color: var(--pageMuted);
          box-shadow: none;
        }
        .btnDanger{
          background: rgba(239, 68, 68, 0.10);
          border-color: rgba(239, 68, 68, 0.35);
        }

        /* Prevent weird squeezing/overlaps in grids */
        .scheduleFormGrid > div,
        .field,
        .laneBody,
        .laneLabel{
        min-width: 0;
        }
        .input, .select, textarea{
        box-sizing: border-box;
        }
        .labelRow{
        flex-wrap: wrap; /* prevents checkbox row from crushing */
        }

        /* Calendar */
        .calHeader{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap: 12px;
          flex-wrap:wrap;
          margin-bottom: 10px;
        }
        .toggleRow{
          display:flex;
          gap:8px;
          align-items:center;
          flex-wrap:wrap;
        }
        .toggleBtn{
          height: 34px;
          border-radius: 999px;
          padding: 0 12px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          cursor:pointer;
          font-weight: 800;
        }
        .toggleBtn[disabled]{
          opacity: 0.55;
          cursor: not-allowed;
        }

        .weekPickerGroup{
        display: flex;
        align-items: center;
        gap: 8px;
        }

        /* Make arrows same height as dropdown for visual balance */
        .weekPickerGroup .toggleBtn{
        height: 40px;
        width: 40px;
        padding: 0;
        }

        .weekPickerRow{
          display:flex;
          gap:10px;
          align-items:center;
          flex-wrap:wrap;
          justify-content:flex-end;
        }
        .smallInput{
          width: 110px;
        }
        .weekSelect{
          min-width: 280px;
        }

        .calGrid{
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
          background: rgba(255,255,255,0.85);
          box-shadow: var(--shadow-sm);
        }
        .calHeadRow{
          display:grid;
          grid-template-columns: 220px repeat(7, minmax(0, 1fr));
          background: rgba(0,0,0,0.04);
          border-bottom: 1px solid var(--border);
        }
        .calHeadCell{
          padding: 10px 10px;
          font-weight: 900;
          font-size: 12px;
          color: var(--text);
          border-right: 1px solid rgba(0,0,0,0.05);
          min-width: 0;
        }
        .calHeadCell:last-child{ border-right: 0; }
        .calLaneRow{
          display:grid;
          grid-template-columns: 220px 1fr;
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }
        .calLaneRow:nth-child(even){
          background: rgba(0,0,0,0.015);
        }
        .calLaneRow:last-child{ border-bottom: 0; }
        .laneLabel{
          padding: 10px 10px;
          border-right: 1px solid rgba(0,0,0,0.06);
          min-width: 0;
        }
        .laneLabel .name{
          font-weight: 900;
          font-size: 13px;
          color: var(--text);
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .laneLabel .kind{
          margin-top: 4px;
          font-size: 11px;
          color: var(--muted);
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .laneBody{
          padding: 8px;
          min-width: 0;
        }
        .laneTrack{
          display:grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 6px;
          margin-bottom: 6px;
        }
        .laneTrack:last-child{ margin-bottom: 0; }

        .schedCard{
          min-width: 0;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: rgba(255,255,255,0.95);
          box-shadow: var(--shadow-sm);
          padding: 8px 10px;
          overflow: hidden;
          border-left-width: 5px;
        }
        .schedCard.status_planned{ border-left-color: rgba(59,130,246,0.55); }
        .schedCard.status_in_progress{ border-left-color: rgba(245,158,11,0.60); }
        .schedCard.status_done{ border-left-color: rgba(34,197,94,0.55); }

        .schedCardTop{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:10px;
        }
        .schedTitle{
          font-weight: 900;
          font-size: 13px;
          color: var(--text);
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
          min-width: 0;
        }
        .schedMeta{
          margin-top: 6px;
          font-size: 11px;
          color: var(--muted);
          font-weight: 700;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .actionIcons{
          display:flex;
          gap:8px;
          align-items:center;
        }
        .iconActionBtn{
          height: 30px;
          width: 30px;
          border-radius: 999px;
          display:grid;
          place-items:center;
          cursor:pointer;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          box-shadow: var(--shadow-sm);
        }
        .iconActionBtn.primary{
          border-color: rgba(79,107,237,0.28);
          color: var(--primary);
        }
        .iconActionBtn.danger{
          background: rgba(239, 68, 68, 0.08);
          border-color: rgba(239, 68, 68, 0.25);
        }

        .iconGlyph{ font-size: 14px; line-height: 1; }
        .badge{
          display:inline-flex;
          align-items:center;
          height: 22px;
          border-radius: 999px;
          padding: 0 10px;
          border: 1px solid var(--border);
          background: rgba(0,0,0,0.04);
          font-size: 11px;
          font-weight: 900;
          color: var(--text);
          margin-top: 6px;
        }
        .badgeGood{ background: rgba(34,197,94,0.10); border-color: rgba(34,197,94,0.25); }
        .badgeWarn{ background: rgba(245,158,11,0.12); border-color: rgba(245,158,11,0.25); }

        .errorText{
          margin-top: 10px;
          color: #b91c1c;
          font-weight: 800;
        }

        /* Modal (no hover behaviors) */
        .modalOverlay{
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.35);
        z-index: 50;

        display: flex;
        align-items: flex-start;
        justify-content: center;

        /* IMPORTANT: pushes modal below fixed topnav */
        --topnavH: 72px;
        padding: calc(16px + var(--topnavH)) 16px 16px 16px;

        overflow: auto;
        -webkit-overflow-scrolling: touch;
        }

        .modalCard{
        width: min(560px, 96vw);
        border-radius: 16px;
        border: 1px solid var(--border);
        background: rgba(255,255,255,0.98);
        box-shadow: var(--shadow-sm);
        padding: 14px;

        /* keep within viewport and allow internal scroll */
        max-height: calc(100vh - (var(--topnavH) + 32px));
        overflow: auto;
        -webkit-overflow-scrolling: touch;

        /* helps centering inside scrollable overlay */
        margin: auto;
        }

        .modalTitle{
          font-weight: 900;
          font-size: 14px;
          margin: 0 0 10px 0;
          color: var(--text);
        }
          
        .modalActions{
        display:flex;
        justify-content:flex-end;
        gap:10px;
        margin-top: 12px;

        /* keep buttons visible */
        position: sticky;
        bottom: 0;

        padding-top: 10px;
        background: rgba(255,255,255,0.98);
        }

        @media (max-width: 980px){
          .scheduleFormGrid{ grid-template-columns: 1fr; }
          .calHeadRow{ grid-template-columns: 180px repeat(7, minmax(0, 1fr)); }
          .calLaneRow{ grid-template-columns: 180px 1fr; }
          .weekSelect{ min-width: 220px; }
        }
        @media (max-width: 980px){
        .calGrid{ overflow-x:auto; }
        .calHeadRow{ min-width: 980px; }
        .calLaneRow{ min-width: 980px; }
        }

        @media (max-width: 520px){
        .inlineRow{
            flex-direction: column;
            gap: 10px;
        }
        .inlineRow .field{
            width: 100%;
        }
        .searchInput{
            width: 100%;
        }
        }

        /* Calendar header layout (stable, responsive) */
        .calHeaderOneRow{
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap: 12px;
            flex-wrap: wrap;
            width: 100%;
            }

            /* Keep right cluster aligned and not wrapping weirdly */
            .calHeaderOneRow .weekPickerRow{
            justify-content:flex-end;
            }

            /* On small screens, stack nicely */
            @media (max-width: 720px){
            .calHeaderOneRow{
                flex-direction: column;
                align-items: stretch;
            }
            .calHeaderOneRow .weekPickerRow{
                justify-content:flex-start;
            }
            .calHeaderOneRow .weekPickerRow .weekSelect{
                flex: 1 1 auto;
                width: 100%;
            }
            }

        .weekPickerRow{
        display:flex;
        align-items:center;
        gap: 10px;
        justify-content:flex-end;
        flex-wrap: wrap;           /* key: prevents weird overflow */
        max-width: 100%;
        }

        .weekPickerRow .smallInput{
        width: 96px;
        min-width: 96px;
        }

        .weekPickerRow .weekSelect{
        flex: 1 1 360px;           /* key: let it resize */
        min-width: 220px;
        max-width: 100%;
        }

        /* Make arrows match input height so alignment looks clean */
        .weekPickerRow .toggleBtn{
        height: 40px;
        }

        /* On small screens, stack nicely */
        @media (max-width: 720px){
        .calHeader{
            flex-direction: column;
            align-items: stretch;
        }
        .weekPickerRow{
            justify-content:flex-start;
        }
        .weekPickerRow .weekSelect{
            flex: 1 1 auto;
            width: 100%;
        }
        }
        .calHeader .weekPickerRow button{
          white-space:nowrap;
          flex:0 0 auto;
        }
        
        /* Prevent page-level horizontal scroll */
        .container{
        overflow-x: hidden;
        }
        
        /* ======================
        PRINT (Schedule)
        NOTE: index.css already handles .noPrint/.printOnly + print isolation.
        This block focuses ONLY on formatting.
        ====================== */

        .schedulePrint{
        color: #111;
        font-size: 12px;
        }

        .schedulePrintHeader{
        margin: 0 0 10px 0;
        }

        .schedulePrintTitle{
        font-weight: 900;
        font-size: 16px;
        margin: 0 0 6px 0;
        }

        .schedulePrintMeta{
        display:flex;
        flex-wrap: wrap;
        gap: 6px 18px;
        font-size: 12px;
        }

        .schedulePrintTable{
        width: 100%;
        border-collapse: collapse;
        }

        .schedulePrintTable th,
        .schedulePrintTable td{
        border: 1px solid #ddd;
        padding: 6px 8px;
        vertical-align: top;

        /* Critical: prevent “letter stacking” */
        white-space: normal;
        overflow-wrap: break-word;   /* wrap long words if needed */
        word-break: normal;          /* do NOT break every character */
        hyphens: auto;
        }

        .schedulePrintTable th{
        background: #f3f4f6;
        font-weight: 900;
        text-align: left;
        }

        /* Group header row (your project label row) */
        .schedulePrintTable .groupRow td{
        background: #f9fafb;
        font-weight: 900;
        }

        /* ======================
           Print Swimlane (screen-style)
        ====================== */
        .schedulePrintSwimWeek{
        margin: 14px 0 18px 0;
        break-inside: avoid;
        page-break-inside: avoid;
        }
        .schedulePrintSwimHeader{
        break-after: avoid;
        page-break-after: avoid;
        }

        .schedulePrintSwimWeek:last-child{
          page-break-after: auto;
        }
        .schedulePrintSwimWeekTitle{
          font-weight: 900;
          font-size: 13px;
          margin: 0 0 10px 0;
        }

        .schedulePrintSwimHeader{
          display: grid;
          grid-template-columns: 220px repeat(7, 1fr);
          gap: 0;
          border: 1px solid #ddd;
          border-bottom: none;
          border-radius: 12px 12px 0 0;
          overflow: hidden;
        }
        .schedulePrintSwimHeader .laneHead{
          background: #f3f4f6;
          font-weight: 900;
          padding: 8px 10px;
          border-right: 1px solid #ddd;
        }
        .schedulePrintSwimHeader .dayHead{
          background: #f3f4f6;
          font-weight: 900;
          padding: 8px 10px;
          border-right: 1px solid #ddd;
        }
        .schedulePrintSwimHeader .dayHead:last-child{
          border-right: none;
        }

        .schedulePrintSwimRow{
          display: grid;
          grid-template-columns: 220px 1fr;
          border-left: 1px solid #ddd;
          border-right: 1px solid #ddd;
          border-bottom: 1px solid #ddd;
        }
        .schedulePrintSwimRow:last-child{
          border-radius: 0 0 12px 12px;
          overflow: hidden;
        }

        .schedulePrintSwimLaneLabel{
          padding: 10px;
          font-weight: 900;
          border-right: 1px solid #ddd;
          background: #fff;
        }

        .schedulePrintSwimTracks{
          padding: 8px 10px;
          background: #fff;
        }

        .schedulePrintSwimTrack{
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 8px;
          margin-bottom: 8px;
        }
        .schedulePrintSwimTrack:last-child{
          margin-bottom: 0;
        }

        .schedulePrintSwimBar{
          border: 1px solid #e5e7eb;
          border-left: 4px solid #90b4ff;
          border-radius: 14px;
          background: #fff;
          padding: 8px 10px;
          box-shadow: 0 1px 0 rgba(0,0,0,0.04);
        }
        .schedulePrintSwimBarTitle{
          font-weight: 900;
          font-size: 13px;
          margin-bottom: 4px;
        }
        .schedulePrintSwimBarSub{
          font-size: 12px;
          opacity: 0.85;
          line-height: 1.25;
        }
        .schedulePrintSwimEmpty{
          opacity: 0.55;
          font-weight: 800;
          padding: 2px 0;
        }

        /* Print calendar (project view) */
        .schedulePrintWeek{
          margin: 14px 0 18px 0;
          page-break-inside: avoid;
        }
        .schedulePrintWeekTitle{
          font-weight: 900;
          font-size: 13px;
          margin: 0 0 8px 0;
        }
        .schedulePrintProject{
          margin: 10px 0 12px 0;
          page-break-inside: avoid;
        }
        .schedulePrintProjectTitle{
          font-weight: 900;
          font-size: 13px;
          margin: 0 0 6px 0;
        }
        .schedulePrintCalTable{
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        .schedulePrintCalTable th,
        .schedulePrintCalTable td{
          border: 1px solid #ddd;
          padding: 6px 8px;
          vertical-align: top;
          white-space: normal;
          overflow-wrap: break-word;
        }
        .schedulePrintCalTable th{
          background: #f3f4f6;
          font-weight: 900;
          text-align: left;
        }
        .schedulePrintDayLabel{
          font-weight: 900;
        }
        .schedulePrintChip{
          margin: 0 0 6px 0;
          padding: 4px 6px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fff;
        }
        .schedulePrintChipTitle{
          font-weight: 900;
          font-size: 12px;
          line-height: 1.2;
        }
        .schedulePrintChipMeta{
          font-size: 11px;
          opacity: 0.8;
          margin-top: 2px;
        }
        .schedulePrintEmpty{
          opacity: 0.55;
          font-weight: 800;
        }


        @media print {
        @page { 
            size: landscape;
            margin: 12mm; 
        }

        html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
        }

        /* Kill the charcoal background blobs during print */
        body::before,
        body::after {
            display: none !important;
        }

        /*
            IMPORTANT:
            index.css makes .printOnly position:absolute.
            That’s what is causing the “half visible / clipped” print preview on Schedule.
            Force it back into normal document flow for printing.
        */
        .printOnly {
            display: block !important;
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: auto !important;
        }

        /* Your page container is position:relative in index.css; remove any print clipping risks */
        .container {
            position: static !important;
            max-width: none !important;
            padding: 0 !important;
            overflow: visible !important;
        }

        /* Make sure the print area itself is normal-flow */
        .schedulePrintArea {
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
        }

        .schedulePrint {
            color: #111 !important;
            font-size: 11px !important;
            line-height: 1.25 !important;
            width: 100% !important;
        }

        .schedulePrintMeta {
            display: block !important;
        }
        .schedulePrintMeta > div {
            display: block !important;
            margin: 2px 0 !important;
            max-width: 100% !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
        }

        .schedulePrintTable {
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
        }

        .schedulePrintTable th,
        .schedulePrintTable td {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
        }
        }

      `}</style>

    <div className="noPrint">
        {/* Header */}
        <div className="scheduleHeaderRow">
          <div className="scheduleHeaderLeft">
            <h2>{tr("schedule.title")}</h2>
            <div className="muted">{tr("schedule.subtitle")}</div>
          </div>

          <div className="scheduleHeaderRight">
            <input
              className="searchInput"
              placeholder={tr("schedule.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Card: Print (collapsed by default) */}
        <div className="scheduleCardSpacer">
          <Card title="">
            {/* Title row (Print + Show/Hide on same line) */}
            <div className="labelRow" style={{ alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontWeight: 900, fontSize: 16 }}>{tr("schedule.printTitle")}</div>

                <button
                type="button"
                className="btn"
                style={{ height: 34, padding: "0 10px" }}
                onClick={() => setPrintCardOpen((v) => !v)}
                aria-expanded={printCardOpen}
                title={printCardOpen ? tr("schedule.hidePrintTools") : tr("schedule.showPrintTools")}
                >
                {printCardOpen ? tr("schedule.hide") : tr("schedule.show")}
                </button>
            </div>

            {printCardOpen ? (
                <>
                {/* Print filters (no 2nd hide button anymore) */}
                <div className="printToolsBox" style={{ marginTop: 10 }}>
                    <div className="printToolsTitle">
                    <div>
                        <div className="label">{tr("schedule.printFilters")}</div>
                        <div className="printToolsHint">{tr("schedule.printFiltersHint")}</div>
                    </div>
                    </div>

                    <div id="printFiltersBody">
                    <div className="calToolsRow">
                        <div className="calToolsLeft">
                        {/* Resource */}
                        <div className="field">
                            <label className="label">{tr("schedule.resource")}</label>
                            <select
                            className="select"
                            value={printResourceId}
                            onChange={(e) => setPrintResourceId(e.target.value)}
                            >
                            <option value={ALL_RESOURCES_FILTER}>{tr("schedule.allResources")}</option>
                            {resourceOptions.map((o) => (
                                <option key={o.id} value={o.id}>
                                {o.label} — {o.kindLabel}
                                </option>
                            ))}
                            </select>
                        </div>

                        {/* Project */}
                        <div className="field">
                            <label className="label">{tr("common.project")}</label>
                            <select
                            className="select"
                            value={printProjectId}
                            onChange={(e) => setPrintProjectId(e.target.value)}
                            >
                            <option value="">{tr("schedule.allProjects")}</option>
                            <option value={NON_PROJECT_FILTER}>{tr("schedule.nonProject")}</option>
                            {(projects || []).map((p: any) => (
                                <option key={p.id} value={p.id}>
                                {projectDisplayLabelPretty(p as any, clients as any)}
                                </option>
                            ))}
                            </select>
                        </div>

                        {/* Date range */}
                        <div className="field">
                            <label className="label">{tr("schedule.startDate")}</label>
                            <input
                            className="input"
                            type="date"
                            value={printStart}
                            onChange={(e) => setPrintStart(e.target.value)}
                            />
                        </div>

                        <div className="field">
                            <label className="label">{tr("schedule.endDate")}</label>
                            <input
                            className="input"
                            type="date"
                            value={printEnd}
                            onChange={(e) => setPrintEnd(e.target.value)}
                            />
                        </div>
                        </div>

                        {/* Layout + privacy */}
                        <div className="field">
                        <label className="label">{tr("schedule.printLayout")}</label>
                        <select
                            className="select"
                            value={printMode}
                            onChange={(e) => setPrintMode((e.target.value as any) || "calendar")}
                        >
                            <option value="swimlane">{tr("schedule.layoutSwimlane")}</option>
                        </select>
                        </div>

                        <div className="field" style={{ minWidth: 220 }}>
                        <label className="label">{tr("schedule.privacy")}</label>

                        <label className="checkRow">
                            <input
                            type="checkbox"
                            checked={printShowWho}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setPrintShowWho(next);
                                if (next) setPrintHideInternalNonProject(false);
                            }}
                            />
                            <span>{tr("schedule.showWhoAssigned")}</span>
                        </label>

                        {!printShowWho && (
                            <label className="checkRow" style={{ marginTop: 6 }}>
                            <input
                                type="checkbox"
                                checked={printHideInternalNonProject}
                                onChange={(e) => setPrintHideInternalNonProject(e.target.checked)}
                            />
                            <span>{tr("schedule.hideInternal")}</span>
                            </label>
                        )}
                        </div>

                        {printError ? (
                        <div className="muted" style={{ color: "#b42318", fontWeight: 900, marginTop: 6 }}>
                            {printError}
                        </div>
                        ) : null}

                        <div className="calToolsRight">
                        <button
                            type="button"
                            className="btn"
                            onClick={() => {
                            setPrintStart(weekStartYmd);
                            setPrintEnd(weekEndYmd);
                            }}
                            title={tr("schedule.resetPrintRange")}
                        >
                            {tr("schedule.resetPrintRangeBtn")}
                        </button>

                        <button
                            type="button"
                            className="btn btnPrimary"
                            onClick={async () => {
                            if (!printStart || !printEnd) {
                                return window.alert(tr("schedule.selectDateRangePrint"));
                            }

                            await loadForPrintRange(printStart, printEnd);
                            setTimeout(() => window.print(), 50);
                            }}
                            disabled={!printStart || !printEnd || printBusy}
                            title={tr("schedule.printSchedule")}
                        >
                            {printBusy ? tr("schedule.preparingBtn") : tr("schedule.printBtn")}
                        </button>
                        </div>
                    </div>
                    </div>
                </div>
                </>
            ) : null}
          </Card>
        </div>

        {/* Card: Add/Edit Schedule Item (collapsible) */}
        <div className="scheduleCardSpacer">
        <Card title="">
            {/* Title row: title left, actions right */}
            <div className="labelRow" style={{ alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 900, fontSize: 16 }}>
                {editingId ? tr("schedule.editItem") : tr("schedule.addItem")}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                type="button"
                className="btn"
                style={{ height: 34, padding: "0 10px" }}
                onClick={() => setScheduleFormOpen((v) => !v)}
                aria-expanded={scheduleFormOpen}
                disabled={busy}
                title={scheduleFormOpen ? tr("schedule.collapseForm") : tr("schedule.expandForm")}
                >
                {scheduleFormOpen ? tr("schedule.hide") : tr("schedule.show")}
                </button>
            </div>
            </div>

            <div ref={formTopRef} style={{ marginTop: 10 }} />

            {scheduleFormOpen ? (
            <>
                <div className="scheduleFormGrid">
                {/* Col 1 */}
                <div className="scheduleFormCol scheduleFormColLeft">
                    <div className="labelRow">
                    <label className="label">
                        {tr("common.project")} <span className="reqStar">*</span>
                    </label>

                    <label
                        className="checkRow"
                        title={tr("schedule.nonProjectTitle")}
                    >
                        <input
                        type="checkbox"
                        checked={nonProjectTask}
                        onChange={(e) => {
                            const next = e.target.checked;
                            setNonProjectTask(next);
                            if (next) setFormProjectId("");
                        }}
                        />
                        <span>{tr("schedule.nonProjectTask")}</span>
                    </label>
                    </div>

                    <select
                    className="select"
                    value={formProjectId}
                    onChange={(e) => setFormProjectId(e.target.value)}
                    disabled={nonProjectTask}
                    >
                    <option value="">
                        {nonProjectTask ? tr("schedule.nonProjectSelectedOpt") : tr("schedule.selectProjectOpt")}
                    </option>

                    {(projects || []).map((p: any) => (
                        <option key={p.id} value={p.id}>
                        {projectDisplayLabelPretty(p as any, clients as any)}
                        </option>
                    ))}
                    </select>

                    <div className="field" style={{ marginTop: 10 }}>
                    <div className="labelRow">
                        <label className="label">
                        {tr("schedule.task")} <span className="reqStar">*</span>
                        </label>

                        <div className="inlineRow" style={{ gap: 8 }}>
                        <button
                            type="button"
                            className="btn"
                            style={{ height: 34, padding: "0 10px" }}
                            onClick={() => setShowAddTask(true)}
                            disabled={busy || taskLibBusy}
                            title={tr("schedule.newTaskTitle")}
                        >
                            {tr("schedule.newTask")}
                        </button>

                        <button
                            type="button"
                            className="btn"
                            style={{ height: 34, padding: "0 10px" }}
                            onClick={() => setShowManageTasks(true)}
                            disabled={busy || taskLibBusy}
                            title={tr("schedule.manageTitle")}
                        >
                            {tr("schedule.manage")}
                        </button>
                        </div>
                    </div>

                    <select
                        className="select"
                        value={taskLibraryId}
                        onChange={(e) => {
                        const nextId = e.target.value;
                        setTaskLibraryId(nextId);
                        const t = (taskLibrary || []).find((x) => x.id === nextId);
                        if (t) setTaskName(t.name);
                        }}
                    >
                        <option value="">{taskLibBusy ? tr("schedule.loadingTasksOpt") : tr("schedule.selectTaskOpt")}</option>

                        {taskLibraryByCategory.map(([cat, list]) => (
                        <optgroup key={cat} label={cat}>
                            {list.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                            ))}
                        </optgroup>
                        ))}
                    </select>

                    {editingId && !taskLibraryId && (taskName || "").trim() ? (
                        <div className="muted" style={{ marginTop: 6, fontWeight: 800 }}>
                        Legacy task name detected:{" "}
                        <span style={{ color: "var(--text)" }}>{taskName}</span>. Pick a
                        library task to normalize.
                        </div>
                    ) : null}

                    {taskLibError ? (
                        <div className="errorText" style={{ marginTop: 6 }}>
                        {taskLibError}
                        </div>
                    ) : null}
                    </div>

                    <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">{tr("schedule.area")}</label>
                    <input
                        className="input"
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                    />
                    </div>
                </div>

                {/* Col 2 */}
                <div className="scheduleFormCol scheduleFormColMid">
                    <div className="inlineRow">
                    <div className="field" style={{ flex: 1 }}>
                        <label className="label">{tr("schedule.startDate")}</label>
                        <input
                        className="input"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="field" style={{ flex: 1 }}>
                        <label className="label">{tr("schedule.endDate")}</label>
                        <input
                        className="input"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    </div>

                    <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">{tr("common.status")}</label>
                    <select
                        className="select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as ScheduleStatus)}
                    >
                        <option value="planned">{tr("schedule.statusPlanned")}</option>
                        <option value="in_progress">{tr("schedule.statusInProgress")}</option>
                        <option value="done">{tr("schedule.statusDone")}</option>
                    </select>
                    </div>

                    <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">{tr("common.notes")}</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                </div>

                {/* Col 3 */}
                <div className="scheduleFormCol scheduleFormColRight">
                    <div className="assignBox">
                    <div className="assignHeader">
                        <h4>{tr("schedule.assign")}</h4>
                        <div className="muted" style={{ fontWeight: 800, fontSize: 12 }}>
                        {assignedIds.length ? tr("schedule.nSelected", { n: assignedIds.length }) : tr("schedule.noneSelected")}
                        </div>
                    </div>

                    {/* Internal group */}
                    <div className="assignItem">
                        <input
                        type="checkbox"
                        checked={assignedIds.includes(INTERNAL_GROUP_ID)}
                        onChange={() => toggleAssigned(INTERNAL_GROUP_ID)}
                        />
                        <div>
                        <div style={{ fontWeight: 900 }}>{tr("schedule.internalResources")}</div>
                        <div className="mutedName">{tr("schedule.crewGroup")}</div>
                        </div>
                    </div>

                    {/* Internal people */}
                    <div className="assignGroup">
                        <button
                        type="button"
                        className="groupTitleBtn"
                        onClick={() => setOpenAssignPanel((p) => (p === "people" ? null : "people"))}
                        >
                        <span>{tr("schedule.individualTeam")}</span>
                        <span className="caret">{openAssignPanel === "people" ? "▾" : "▸"}</span>
                        </button>

                        {openAssignPanel === "people" && (
                        <div className="assignList">
                            {internalPeople.length === 0 ? (
                            <div className="muted" style={{ padding: "4px 2px" }}>
                                {tr("schedule.noInternalResources")}
                            </div>
                            ) : (
                            internalPeople.map((r: any) => (
                                <label key={r.id} className="assignItem">
                                <input
                                    type="checkbox"
                                    checked={assignedIds.includes(r.id)}
                                    onChange={() => toggleAssigned(r.id)}
                                />
                                <div>
                                    <div style={{ fontWeight: 900 }}>{r.name}</div>
                                    {(r.role || r.phone) && (
                                    <div className="mutedName">
                                        {[r.role, r.phone].filter(Boolean).join(" • ")}
                                    </div>
                                    )}
                                </div>
                                </label>
                            ))
                            )}
                        </div>
                        )}
                    </div>

                    {/* Subcontractors */}
                    <div className="assignGroup">
                        <button
                        type="button"
                        className="groupTitleBtn"
                        onClick={() => setOpenAssignPanel((p) => (p === "subs" ? null : "subs"))}
                        >
                        <span>{tr("schedule.subcontractors")}</span>
                        <span className="caret">{openAssignPanel === "subs" ? "▾" : "▸"}</span>
                        </button>

                        {openAssignPanel === "subs" && (
                        <div className="assignList">
                            {subcontractors.length === 0 ? (
                            <div className="muted" style={{ padding: "4px 2px" }}>
                                {tr("schedule.noSubcontractors")}
                            </div>
                            ) : (
                            subcontractors.map((r: any) => (
                                <label key={r.id} className="assignItem">
                                <input
                                    type="checkbox"
                                    checked={assignedIds.includes(r.id)}
                                    onChange={() => toggleAssigned(r.id)}
                                />
                                <div style={{ fontWeight: 900 }}>{r.name}</div>
                                </label>
                            ))
                            )}
                        </div>
                        )}
                    </div>

                    <div className="formActions">
                        {editingId && (
                        <button
                            className="btn"
                            type="button"
                            onClick={() => {
                                resetForm();
                                setScheduleFormOpen(false);
                            }}
                            disabled={busy}
                            >
                            {tr("schedule.cancelBtn")}
                        </button>
                        )}
                        <button
                        className="btn btnPrimary"
                        type="button"
                        onClick={save}
                        disabled={!canSubmit}
                        >
                        {editingId ? tr("schedule.saveChanges") : tr("schedule.addToSchedule")}
                        </button>
                    </div>
                    </div>
                </div>
                </div>

                {error && <div className="errorText">{error}</div>}
            </>
            ) : null}
        </Card>
        </div>

        {/* Card: Calendar */}
        <div className="scheduleCardSpacer">
        <Card title={tr("schedule.calendarTitle")}>
            {/* One-row header: Week/Month + Hide empty + Add task + Week picker */}
            <div className="calHeader">
            <div className="calHeaderOneRow">
                {/* Left cluster */}
                <div className="toggleRow">
                <button
                    type="button"
                    className={`toggleBtn ${viewMode === "week" ? "active" : ""}`}
                    onClick={() => setViewMode("week")}
                >
                    {tr("schedule.viewWeek")}
                </button>

                <button
                    type="button"
                    className={`toggleBtn ${viewMode === "month" ? "active" : ""}`}
                    disabled
                    title={tr("schedule.monthComingSoon")}
                    onClick={() => setViewMode("month")}
                >
                    {tr("schedule.viewMonth")}
                </button>

                <label className="checkRow" style={{ marginLeft: 8 }}>
                    <input
                    type="checkbox"
                    checked={hideEmptyLanes}
                    onChange={(e) => setHideEmptyLanes(e.target.checked)}
                    />
                    <span>{tr("schedule.hideEmptyResources")}</span>
                </label>
                </div>

                {/* Right cluster */}
                <div className="weekPickerRow" style={{ marginBottom: 0 }}>
                <button
                    type="button"
                    className="btn btnPrimary"
                    style={{ height: 40, padding: "0 12px" }}
                    onClick={() => {
                    if (!editingId) resetForm();
                    setScheduleFormOpen(true);
                    setTimeout(
                        () =>
                        formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
                        0
                    );
                    }}
                    disabled={busy}
                    title={tr("schedule.addTaskTitle")}
                >
                    {tr("schedule.addTask")}
                </button>

                <input
                    className="input smallInput"
                    type="number"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value) || new Date().getFullYear())}
                    disabled={viewMode !== "week"}
                />

                <div className="weekPickerGroup">
                    <button
                    type="button"
                    className="toggleBtn"
                    onClick={() => stepWeek(-1)}
                    disabled={viewMode !== "week"}
                    title={tr("schedule.prevWeek")}
                    >
                    ←
                    </button>

                    <select
                    className="select weekSelect"
                    value={weekNumber}
                    onChange={(e) => setWeekNumber(Number(e.target.value) || 1)}
                    disabled={viewMode !== "week"}
                    >
                    {weekOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                        {opt.label}
                        </option>
                    ))}
                    </select>

                    <button
                    type="button"
                    className="toggleBtn"
                    onClick={() => stepWeek(1)}
                    disabled={viewMode !== "week"}
                    title={tr("schedule.nextWeek")}
                    >
                    →
                    </button>
                </div>
                </div>
            </div>
            </div>

            <div className="muted" style={{ fontWeight: 800, marginBottom: 10 }}>
            {tr("schedule.showingRange", { start: weekStartYmd, end: weekEndYmd })}{busy ? tr("schedule.loadingInline") : ""}
            </div>

            <div className="calGrid">
            {/* header row */}
            <div className="calHeadRow">
                <div className="calHeadCell">{tr("schedule.resource")}</div>
                {weekDays.map((d) => (
                <div key={d.key} className="calHeadCell">
                    {d.label} <span style={{ opacity: 0.7 }}>{d.ymd.slice(5)}</span>
                </div>
                ))}
            </div>

            {/* lane rows */}
            {calendarRowsVisible.map(({ lane, tracks }) => {
                const kindLabel =
                lane.kind === "group" ? tr("schedule.kindGroup") : lane.kind === "sub" ? tr("schedule.kindSub") : tr("schedule.kindInternal");
                return (
                <div key={lane.key} className="calLaneRow">
                    <div className="laneLabel">
                    <div className="name">{lane.label}</div>
                    <div className="kind">{kindLabel}</div>
                    </div>

                    <div className="laneBody">
                    {tracks.length === 0 ? (
                        <div className="muted" style={{ padding: "6px 2px", fontWeight: 800 }}>
                        —
                        </div>
                    ) : (
                        tracks.map((t, idx) => (
                        <div key={`${lane.key}_t_${idx}`} className="laneTrack">
                            {t.map((c) => {
                            const it = c.item;

                            const proj = isNonProject(it) ? tr("schedule.nonProjectBadge") : prettyProjectLabel(it.project_id);
                            const meta = proj ? `${proj}${it.area ? ` • ${it.area}` : ""}` : it.area || "";

                            return (
                                <div
                                key={c.id}
                                className={`schedCard status_${it.status}`}
                                style={{ gridColumn: `${c.startIdx + 1} / ${c.endIdx + 2}` }}
                                title={`${taskNameForItem(it)} (${it.start_date} → ${it.end_date})`}
                                >
                                <div className="schedCardTop">
                                    <div style={{ minWidth: 0 }}>
                                    <div className="schedTitle">{taskNameForItem(it)}</div>
                                    {meta ? <div className="schedMeta">{meta}</div> : null}
                                    <div className={statusBadge(it.status)}>{it.status.replace("_", " ")}</div>
                                    </div>

                                    <div className="actionIcons">
                                    <button
                                        className="iconActionBtn"
                                        type="button"
                                        title={tr("common.edit")} aria-label={tr("common.edit")}
                                        onClick={() => beginEdit(it)}
                                    >
                                        <IconPencil />
                                    </button>

                                    <button
                                        className="iconActionBtn danger"
                                        type="button"
                                        title={tr("common.delete")} aria-label={tr("common.delete")}
                                        onClick={() => handleDelete(it.id)}
                                    >
                                        <IconTrash />
                                    </button>
                                    </div>
                                </div>
                                </div>
                            );
                            })}
                        </div>
                        ))
                    )}
                    </div>
                </div>
                );
            })}
            </div>
        </Card>
        </div>

        {/* Add New Task Modal */}
        {showAddTask && (
          <div
            className="modalOverlay"
            role="dialog"
            aria-modal="true"
            aria-label={tr("schedule.addNewTask")}
            onClick={() => {
              if (!newTaskBusy) setShowAddTask(false);
            }}
          >
            <div className="modalCard" onClick={(e) => e.stopPropagation()}>
              <h3 className="modalTitle">{tr("schedule.addNewTask")}</h3>

              <div className="field" style={{ marginTop: 8 }}>
                <label className="label">
                  {tr("schedule.taskName")} <span className="reqStar">*</span>
                </label>
                <input
                  className="input"
                  value={newTaskName}
                  onChange={(e) => setNewTaskName(e.target.value)}
                  placeholder={tr("schedule.taskNamePlaceholder")}
                  disabled={newTaskBusy}
                />
              </div>

              <div className="field" style={{ marginTop: 10 }}>
                <label className="label">
                  {tr("schedule.category")} <span className="reqStar">*</span>
                </label>
                <input
                  className="input"
                  value={newTaskCategory}
                  onChange={(e) => setNewTaskCategory(e.target.value)}
                  placeholder={tr("schedule.categoryPlaceholder")}
                  disabled={newTaskBusy}
                />
                <div className="muted" style={{ marginTop: 6, fontWeight: 800 }}>
                  {tr("schedule.categoryTip")}
                </div>
              </div>

              <div className="modalActions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowAddTask(false)}
                  disabled={newTaskBusy}
                >
                  {tr("schedule.cancelBtnModal")}
                </button>

                <button
                  type="button"
                  className="btn btnPrimary"
                  onClick={createTask}
                  disabled={
                    newTaskBusy ||
                    !(newTaskName || "").trim() ||
                    !(newTaskCategory || "").trim()
                  }
                >
                  {newTaskBusy ? tr("schedule.addingBtn") : tr("schedule.addTaskBtn2")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Tasks Modal */}
        {showManageTasks && (
          <div
            className="modalOverlay"
            role="dialog"
            aria-modal="true"
            aria-label={tr("schedule.manageTasks")}
            onClick={() => {
              if (!taskEditBusy) {
                setShowManageTasks(false);
                cancelTaskEdit();
              }
            }}
          >
            <div className="modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="labelRow" style={{ alignItems: "center" }}>
                <h3 className="modalTitle" style={{ marginBottom: 0 }}>
                  {tr("schedule.manageTasksTitle")}
                </h3>
                <button
                  type="button"
                  className="btn"
                  style={{ height: 34, padding: "0 10px" }}
                  onClick={() => {
                    setShowManageTasks(false);
                    cancelTaskEdit();
                  }}
                  disabled={taskEditBusy}
                >
                  {tr("schedule.closeBtn")}
                </button>
              </div>

              <div className="muted" style={{ fontWeight: 800, marginTop: 6 }}>
                {tr("schedule.manageTasksNote")}
              </div>

              <div className="field" style={{ marginTop: 10 }}>
                <label className="label">{tr("schedule.searchLabel")}</label>
                <input
                  className="input"
                  value={taskManagerSearch}
                  onChange={(e) => setTaskManagerSearch(e.target.value)}
                  placeholder={tr("schedule.taskSearchPlaceholder")}
                  disabled={taskEditBusy}
                />
              </div>

              {/* Edit panel */}
              {taskEditId && (
                <div className="assignBox" style={{ marginTop: 12 }}>
                  <div className="assignHeader">
                    <h4>{tr("schedule.editTaskTitle")}</h4>
                    <div className="muted" style={{ fontWeight: 900 }}>
                      {taskEditBusy ? "Saving..." : ""}
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">{tr("schedule.taskNameLabel")}</label>
                    <input
                      className="input"
                      value={taskEditName}
                      onChange={(e) => setTaskEditName(e.target.value)}
                      disabled={taskEditBusy}
                    />
                  </div>

                  <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">{tr("schedule.categoryLabel")}</label>
                    <input
                      className="input"
                      value={taskEditCategory}
                      onChange={(e) => setTaskEditCategory(e.target.value)}
                      disabled={taskEditBusy}
                    />
                  </div>

                  <div className="modalActions">
                    <button className="btn" type="button" onClick={cancelTaskEdit} disabled={taskEditBusy}>
                      {tr("schedule.cancelBtnModal")}
                    </button>
                    <button className="btn btnPrimary" type="button" onClick={saveTaskEdit} disabled={taskEditBusy}>
                      {tr("schedule.saveBtn")}
                    </button>
                  </div>
                </div>
              )}

              {/* List */}
              <div style={{ marginTop: 12 }}>
                {taskManagerByCategory.map(([cat, list]) => (
                  <div key={cat} className="assignBox" style={{ marginBottom: 10 }}>
                    <div className="assignHeader">
                      <h4>{cat}</h4>
                      <div className="muted" style={{ fontWeight: 900 }}>
                        {list.length} task{list.length === 1 ? "" : "s"}
                      </div>
                    </div>

                    <div className="assignList" style={{ gap: 10 }}>
                      {list.map((t) => {
                        const editable = canEditTask(t);
                        const badge = isGlobalTask(t) ? "GLOBAL" : String(t.tenant_id || "").toUpperCase();

                        return (
                          <div key={t.id} className="assignItem" style={{ justifyContent: "space-between" }}>
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 900,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t.name}
                              </div>
                              <div className="mutedName">
                                <span style={{ fontWeight: 900 }}>{badge}</span>
                              </div>
                            </div>

                            <div className="actionIcons">
                              <button
                                className="iconActionBtn"
                                type="button"
                                title={editable ? tr("common.edit") : tr("schedule.editNoPerm")}
                                aria-label={tr("schedule.editTask")}
                                onClick={() => editable && beginTaskEdit(t)}
                                disabled={!editable || taskEditBusy}
                              >
                                        <IconPencil />
                              </button>

                              <button
                                className="iconActionBtn danger"
                                type="button"
                                title={editable ? tr("common.delete") : tr("schedule.deleteNoPerm")}
                                aria-label={tr("schedule.deleteTask")}
                                onClick={() => editable && deleteTask(t)}
                                disabled={!editable || taskEditBusy}
                              >
                                        <IconTrash />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
     </div>

      
    {/* Print-only output */}
      <div className="printOnly schedulePrint">
        <div className="schedulePrintArea schedulePrint">
          <div className="schedulePrintHeader">
            <div className="schedulePrintTitle">{tr("schedule.printWorkSchedule")}</div>

            <div className="schedulePrintMeta">
              <div>
                <b>{tr("schedule.printResourceLabel")}:</b>{" "}
                {printResourceId === ALL_RESOURCES_FILTER
                  ? tr("schedule.printAllResources")
                  : resourceOptions.find((x) => x.id === printResourceId)?.label || "—"}
              </div>

              <div>
                <b>{tr("schedule.printDateRangeLabel")}:</b> {printStart || "—"} → {printEnd || "—"}
              </div>

              <div>
                <b>{tr("schedule.printProjectLabel")}:</b>{" "}
                {!printProjectId
                  ? tr("schedule.printAllProjects")
                  : printProjectId === NON_PROJECT_FILTER
                  ? tr("schedule.printNonProject")
                  : prettyProjectLabel(printProjectId)}
              </div>

              <div>
                <b>{tr("schedule.printTotalItemsLabel")}:</b> {printItems.length}
              </div>

              <div className="muted" style={{ marginTop: 6, fontWeight: 800 }}>
                {tr("schedule.printSwimNote")}
              </div>
            </div>
          </div>

          {/* Swimlane calendar (screen-style) */}
          <div>
            {printSwimWeeks.length === 0 ? (
              <div style={{ padding: 10 }}>{tr("schedule.printNoItemsMsg")}</div>
            ) : (
              printSwimWeeks.map((w) => (
                <div key={w.weekStartYmd} className="schedulePrintSwimWeek">
                  <div className="schedulePrintSwimWeekTitle">
                    {tr("schedule.printWeekOf", { start: w.weekStartYmd, end: w.weekEndYmd })}
                  </div>

                  <div className="schedulePrintSwimHeader">
                    <div className="laneHead">{printShowWho ? tr("schedule.printLaneLabelWho") : tr("schedule.printLaneLabelProject")}</div>
                    {w.days.map((d) => (
                        <div key={d.ymd} className="dayHead">
                        {d.label} <span style={{ opacity: 0.75 }}>{d.ymd.slice(5)}</span>
                        </div>
                    ))}
                  </div>


            {printShowWho ? (
            // ======================
            // Swimlane by Resource/Lane (internal)
            // ======================
            (w.rows || []).length === 0 ? (
                <div className="schedulePrintSwimRow">
                <div className="schedulePrintSwimLaneLabel">—</div>
                <div className="schedulePrintSwimTracks">
                    <div className="schedulePrintSwimEmpty">{tr("schedule.printNoLanesWeek")}</div>
                </div>
                </div>
            ) : (
            w.rows.map((r) => (
                <div key={r.lane.key} className="schedulePrintSwimRow">
                    <div className="schedulePrintSwimLaneLabel">{r.lane.label}</div>

                    <div className="schedulePrintSwimTracks">
                    {(r.tracks || []).length === 0 ? (
                        <div className="schedulePrintSwimEmpty">—</div>
                    ) : (
                        (r.tracks || []).map((track, idx) => (
                        <div key={idx} className="schedulePrintSwimTrack">
                            {track.map((c) => {
                            const it = c.item;
                            const title = taskNameForItem(it);
                            const sub = isNonProject(it)
                                ? "Non-Project"
                                : prettyProjectLabel(it.project_id) || "";
                            const area = (it.area || "").trim();
                            const subline = [sub, area].filter(Boolean).join(" • ");

                            const colStart = c.startIdx + 1;
                            const colEnd = c.endIdx + 2;

                            return (
                                <div
                                key={c.id}
                                className="schedulePrintSwimBar"
                                style={{ gridColumn: `${colStart} / ${colEnd}` }}
                                >
                                <div className="schedulePrintSwimBarTitle">{title}</div>
                                {subline ? <div className="schedulePrintSwimBarSub">{subline}</div> : null}
                                </div>
                            );
                            })}
                        </div>
                        ))
                    )}
                    </div>
                </div>
                ))
            )
            ) : (

            // ======================
            // Swimlane by Project/Site (client share)
            // ======================
            (() => {
                const weekItems = (printItems || []).filter((it) =>
                overlaps(it, w.weekStartYmd, w.weekEndYmd)
                );

                const projMap = new Map<string, ScheduleItem[]>();
                weekItems.forEach((it) => {
                if (printHideInternalNonProject && isNonProject(it)) return;

                const label = isNonProject(it)
                    ? "Non-Project"
                    : prettyProjectLabel(it.project_id) || "Unknown Project";

                const arr = projMap.get(label) || [];
                arr.push(it);
                projMap.set(label, arr);
                });

                const projRows = Array.from(projMap.entries())
                .map(([label, list]) => {
                    const cards: CardSpan[] = (list || []).map((it) => {
                    const startIdx = clamp(daysBetweenYmd(w.weekStartYmd, it.start_date), 0, 6);
                    const endIdx = clamp(daysBetweenYmd(w.weekStartYmd, it.end_date), 0, 6);
                    return { id: it.id, startIdx, endIdx, item: it };
                    });
                    const tracks = buildTracks(cards);
                    return { label, tracks };
                })
                .sort((a, b) => a.label.localeCompare(b.label));

                if (projRows.length === 0) {
                return (
                    <div className="schedulePrintSwimRow">
                    <div className="schedulePrintSwimLaneLabel">—</div>
                    <div className="schedulePrintSwimTracks">
                        <div className="schedulePrintSwimEmpty">{tr("schedule.printNoProjectItems")}</div>
                    </div>
                    </div>
                );
                }

                return projRows.map((r) => (
                <div key={r.label} className="schedulePrintSwimRow">
                    <div className="schedulePrintSwimLaneLabel">{r.label}</div>

                    <div className="schedulePrintSwimTracks">
                    {(r.tracks || []).length === 0 ? (
                        <div className="schedulePrintSwimEmpty">—</div>
                    ) : (
                        (r.tracks || []).map((track, idx) => (
                        <div key={idx} className="schedulePrintSwimTrack">
                            {track.map((c) => {
                            const it = c.item;
                            const title = taskNameForItem(it);

                            const area = (it.area || "").trim();
                            const subline = area ? area : "";

                            const colStart = c.startIdx + 1;
                            const colEnd = c.endIdx + 2;

                            return (
                                <div
                                key={c.id}
                                className="schedulePrintSwimBar"
                                style={{ gridColumn: `${colStart} / ${colEnd}` }}
                                >
                                <div className="schedulePrintSwimBarTitle">{title}</div>
                                {subline ? <div className="schedulePrintSwimBarSub">{subline}</div> : null}
                                </div>
                            );
                            })}
                        </div>
                        ))
                    )}
                    </div>
                </div>
                ));
            })()
            )}
               
                </div>
              ))
            )}

            {/* Detailed table (print once after all weeks) */}
            <div style={{ height: 14 }} />

            <table className="schedulePrintTable">
              <colgroup>
                <col className="colStart" />
                <col className="colEnd" />
                <col className="colTask" />
                <col className="colProject" />
                <col className="colArea" />
                <col className="colStatus" />
                <col className="colNotes" />
              </colgroup>

              <thead>
                <tr>
                  <th>{tr("schedule.colStart")}</th>
                  <th>{tr("schedule.colEnd")}</th>
                  <th>{tr("schedule.colTask")}</th>
                  <th>{tr("schedule.colProject")}</th>
                  <th>{tr("schedule.colArea")}</th>
                  <th>{tr("schedule.colStatus")}</th>
                  <th>{tr("schedule.colNotes")}</th>
                </tr>
              </thead>

              <tbody>
                {printItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 10 }}>
                      No scheduled items for this selection.
                    </td>
                  </tr>
                ) : (
                  printGroups.map((g) => (
                    <React.Fragment key={g.projectLabel}>
                      <tr>
                        <td colSpan={7} style={{ background: "#f9fafb", fontWeight: 900 }}>
                          {g.projectLabel}
                        </td>
                      </tr>

                      {g.items.map((it) => (
                        <tr key={it.id}>
                          <td>{it.start_date}</td>
                          <td>{it.end_date}</td>
                          <td>{taskNameForItem(it)}</td>
                          <td>{isNonProject(it) ? "Non-Project" : prettyProjectLabel(it.project_id)}</td>
                          <td>{it.area || ""}</td>
                          <td>{String(it.status || "").replace("_", " ")}</td>
                          <td>{it.scope_notes || ""}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>


          </div>
        </div>
      </div>
    </div>
  );
}

